// Session state (FR-03): the access token lives in memory only; the refresh
// token is an HttpOnly cookie. On load and before expiry the app calls
// /auth/refresh with the CSRF header (SEC-07). Refreshes are single-flight:
// presenting a refresh token twice revokes the whole family (P1-2).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Middleware } from "openapi-fetch";
import { api } from "../../api/client";

type Tokens = { accessToken: string; expiresAt: number };

let current: Tokens | null = null;
let inflight: Promise<Tokens | null> | null = null;
const listeners = new Set<(t: Tokens | null) => void>();

function setTokens(t: Tokens | null) {
  current = t;
  listeners.forEach((l) => l(t));
}

export function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)(?:__Host-)?csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : "";
}

/** refresh rotates the refresh cookie; only one refresh runs at a time. */
export function refresh(): Promise<Tokens | null> {
  if (inflight) return inflight;
  const csrf = csrfToken();
  if (!csrf) return Promise.resolve(null);
  inflight = (async () => {
    try {
      const { data } = await api.POST("/auth/refresh", { params: { header: { "X-CSRF-Token": csrf } } });
      const t = data ? { accessToken: data.access_token, expiresAt: Date.parse(data.expires_at) } : null;
      setTokens(t);
      return t;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function adoptSession(accessToken: string, expiresAt: string) {
  setTokens({ accessToken, expiresAt: Date.parse(expiresAt) });
}

export function clearSession() {
  setTokens(null);
}

/** Adds the bearer token, refreshing it first when it is about to expire. */
export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    if (request.headers.has("Authorization") || request.url.includes("/auth/refresh")) return request;
    let t = current;
    if (t && t.expiresAt - Date.now() < 30_000) t = await refresh();
    if (t) request.headers.set("Authorization", `Bearer ${t.accessToken}`);
    return request;
  },
};
api.use(authMiddleware);

type SessionState = {
  status: "loading" | "signed-in" | "signed-out";
  signOut: () => Promise<void>;
  signOutEverywhere: () => Promise<void>;
};
const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [tokens, setLocal] = useState<Tokens | null>(current);
  const [loading, setLoading] = useState(true);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const l = (t: Tokens | null) => setLocal(t);
    listeners.add(l);
    void refresh().finally(() => setLoading(false));
    return () => {
      listeners.delete(l);
    };
  }, []);

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (tokens) {
      timer.current = window.setTimeout(() => void refresh(), Math.max(tokens.expiresAt - Date.now() - 60_000, 5_000));
    }
    return () => window.clearTimeout(timer.current);
  }, [tokens]);

  const signOut = useCallback(async () => {
    await api.POST("/auth/logout", { params: { header: { "X-CSRF-Token": csrfToken() } } });
    clearSession();
  }, []);
  const signOutEverywhere = useCallback(async () => {
    await api.POST("/auth/logout-all");
    clearSession();
  }, []);

  const value = useMemo<SessionState>(
    () => ({ status: loading ? "loading" : tokens ? "signed-in" : "signed-out", signOut, signOutEverywhere }),
    [loading, tokens, signOut, signOutEverywhere],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}

// Onboarding tokens are scoped to onboarding only; they are kept for the tab's
// lifetime so a reload does not lose progress (ADR 0018).
const ONBOARDING_KEY = "onboarding";

export function saveOnboarding(token: string) {
  try {
    sessionStorage.setItem(ONBOARDING_KEY, token);
  } catch {
    /* storage unavailable: the token lives only for this page */
  }
}

export function onboardingToken(): string | null {
  try {
    return sessionStorage.getItem(ONBOARDING_KEY);
  } catch {
    return null;
  }
}

export function clearOnboarding() {
  try {
    sessionStorage.removeItem(ONBOARDING_KEY);
  } catch {
    /* ignore */
  }
}

export function onboardingHeaders(): { Authorization: string } {
  return { Authorization: `Bearer ${onboardingToken() ?? ""}` };
}
